/**
 * The decisions behind the "Add user" door, kept free of the DOM and of
 * Firebase so they can be tested directly. `add-user.ts` renders; this file
 * decides. Everything here is a pure function of what the admin ticked.
 */
import type { EnvironmentSpec } from "./cf-client";

/** One environment the admin ticked, with the role chosen inside it. */
export interface Pick {
  spec: EnvironmentSpec;
  role: string;
}

/**
 * What happened in ONE environment. Granting a set means each environment
 * succeeds or fails on its own, so there is no single outcome to report —
 * and a failure in the last must never hide a success in the first.
 */
export interface Outcome {
  environment: string;
  ok: boolean;
  message: string;
}

export interface FormRules {
  /** A family name is needed only to MINT a person record — production only. */
  needsFamily: boolean;
  /** An expiry belongs only to a guest being granted the family role. */
  needsExpiry: boolean;
  /**
   * ☠️ Production and a tenant environment are separate account pools: the
   * person will hold TWO sign-ins. Shown only when both kinds are ticked —
   * a standing caveat on every grant is noise people learn to skip.
   */
  twoPools: boolean;
  canSave: boolean;
}

/**
 * Each rule mirrors one the server enforces anyway: the form makes the
 * server's answer visible early, it never substitutes for it.
 */
export function formRules(picks: Pick[]): FormRules {
  const grantsByRole = picks.some((p) => p.spec.mechanism === "role");
  const mintsAnAccount = picks.some((p) => p.spec.mechanism === "invite");
  return {
    needsFamily: grantsByRole,
    needsExpiry: picks.some((p) => p.spec.mechanism === "invite" && p.role === "family"),
    twoPools: grantsByRole && mintsAnAccount,
    canSave: picks.length > 0,
  };
}

/**
 * Grant every pick, one at a time, reporting after each.
 *
 * ☠️ SEQUENTIAL, not Promise.all. Each grant is a server write, the
 * production path can raise a confirm() dialog, and several touch the same
 * person record — concurrency buys a second of wall clock and risks
 * interleaving writes to one document.
 *
 * `grantOne` must not throw (the door's implementation catches), but a
 * throw is still contained here: one bad environment must never silently
 * cancel the others the admin asked for.
 */
export async function grantAll(
  picks: Pick[],
  grantOne: (pick: Pick) => Promise<Outcome>,
  onProgress: (outcomes: Outcome[]) => void,
): Promise<Outcome[]> {
  const outcomes: Outcome[] = [];
  for (const pick of picks) {
    let outcome: Outcome;
    try {
      outcome = await grantOne(pick);
    } catch (err) {
      outcome = {
        environment: pick.spec.environment,
        ok: false,
        message: err instanceof Error ? err.message : "Couldn't add that user.",
      };
    }
    outcomes.push(outcome);
    onProgress([...outcomes]);
  }
  return outcomes;
}
